import type {BannerDataText} from '@core-client/ads/banner/data/BannerDataText';
import type {IBannerRequestResult} from '../transport/IBannerTransport';
import {isDebugMode} from '../util/BannerCommon';
import {getBannerStatLogger} from '../util/BannerStat';

const ON_REQUEST = 'onRequest';
const ON_ADS_SUCCESS = 'onAdsSuccess';
const ON_NO_ADS = 'onNoAds';
const ON_SCRIPT_ERROR = 'onScriptError';
const ON_LOAD_SUCCESS = 'onLoadSuccess';
const ON_LOAD_ERROR = 'onLoadError';

const ON_AD_CLOSED = 'onAdClosed';
const ON_AD_FALLBACK = 'onAdFallback';
const ON_AD_LOADED = 'onAdLoaded';
const ON_AD_SHOWN = 'onAdShown';
const ON_AD_WAIT_TIMEOUT = 'onAdWaitTimeout';

export interface IBannerResponse {
    /**
     * тип транспорта
     */
    transport: string,
    /**
     * id слота
     */
    slot: string,
    /**
     * см. BannerPlace.java
     */
    place: string,
    /**
     * элемент-контейнер баннера
     */
    el: HTMLElement,
    /**
     * Результат запроса баннера.
     */
    result: IBannerRequestResult;
}

type IBannerEventListener = (data: IBannerResponse) => unknown;

interface IBannerEventListenerRemoveData {
    event: string;
    listener: IBannerEventListener;
}

let LISTENERS_BY_EVENTS: {[key: string]: IBannerEventListener[]} = {};

/**
 * Вызывается при запросе баннера
 */
export function onRequest(resp: IBannerResponse) {
    publishEvent(ON_REQUEST, resp);
}

/**
 * Вызывается для каждого непустого баннера в ответе от RB
 */
export function onAdsSuccess(resp: IBannerResponse) {
    publishEvent(ON_ADS_SUCCESS, resp);
}

/**
 * Вызывается для каждого пустого баннера в ответе от RB
 */
export function onNoAds(resp: IBannerResponse) {
    publishEvent(ON_NO_ADS, resp);
}

/**
 * Вызывается при ошибке выполнения inline-скриптов баннера
 */
export function onScriptError(resp: IBannerResponse) {
    publishEvent(ON_SCRIPT_ERROR, resp);
}

export function onLoadSuccess(resp: IBannerResponse) {
    publishEvent(ON_LOAD_SUCCESS, resp);
}

export function onLoadError(resp: IBannerResponse) {
    publishEvent(ON_LOAD_ERROR, resp);
}

/**
 * Вызывается при закрытии баннера пользователем.
 */
export function onAdClosed(resp: IBannerResponse) {
    publishEvent(ON_AD_CLOSED, resp);
}

/**
 * Вызывается при замене отображаемого баннера.
 */
export function onAdFallback(resp: IBannerResponse) {
    let eventName = ON_AD_FALLBACK;
    const { result } = resp;
    if (result && (result as BannerDataText).rbJson && (result as BannerDataText).rbJson![0].source) {
        eventName += '.' + (result as BannerDataText).rbJson![0].source;
    }
    publishEvent(eventName, resp);
}

/**
 * Вызывается после загрузки контента баннера.
 */
export function onAdLoaded(resp: IBannerResponse) {
    publishEvent(ON_AD_LOADED, resp);
}

/**
 * Вызывается после отображения баннера.
 */
export function onAdShown(resp: IBannerResponse) {
    publishEvent(ON_AD_SHOWN, resp);
}

/**
 * Вызывается после окончания периода ожидания загрузки контента баннера.
 */
export function onAdWaitTimeout(resp: IBannerResponse) {
    publishEvent(ON_AD_WAIT_TIMEOUT, resp);
}

/**
 * Добавляет слушателя к определенному событию.
 * Возвращает true, если слушатель был добавлен к событию, false - иначе.
 */
function addNewListenerForEvent(eventName: string, newListener: IBannerEventListener): boolean {
    if (!eventName || typeof newListener !== 'function') {
        return false;
    }
    if (!LISTENERS_BY_EVENTS.hasOwnProperty(eventName)) {
        LISTENERS_BY_EVENTS[eventName] = [];
    }
    const listeners = LISTENERS_BY_EVENTS[eventName];
    listeners.push(newListener);
    return true;
}


/**
 * Удаляет слушателя для определенного события
 */
function removeListenerForEvent(eventName: string, listenerForRemove: IBannerEventListener) {
    if (!eventName || !LISTENERS_BY_EVENTS.hasOwnProperty(eventName)) {
        return;
    }
    const listeners = LISTENERS_BY_EVENTS[eventName];
    const removeIdx = listeners.indexOf(listenerForRemove);
    if (removeIdx >= 0) {
        listeners.splice(removeIdx, 1);
    }
}


/**
 * Осуществляет подписку слушателей на события баннеров для определенного места.
 * @param {string} place [при возникновении событий, относящихся к данному месту, будут вызываться callback-функции];
 * @param {Object.<string, Function>} listenersByEvents, где ключ - название события, значение - callback-функция;
 * @returns {Object} объект подписки, у которого можно вызвать метод remove.
 * При его вызове добавленные слушатели событий перестают "слушать".
 */
export function subscribeToEvents(place: string, listenersByEvents: {[key: string]: IBannerEventListener}): object {
    let removeInfo: IBannerEventListenerRemoveData[] = [];
    // добавляем слушателей на конкретные события и запоминаем их для последующего удаления
    for (let eventName in listenersByEvents) {
        if (listenersByEvents.hasOwnProperty(eventName)) {
            const newListener = createDelegate(place, listenersByEvents[eventName]);
            if (addNewListenerForEvent(eventName, newListener)) {
                removeInfo.push({event: eventName, listener: newListener});
            }
        }
    }
    return {
        remove: () => {
            while (removeInfo.length > 0) {
                const info = removeInfo.pop();
                if (info) {
                    removeListenerForEvent(info.event, info.listener);
                }
            }
        }
    };
}

function createDelegate(targetPlace: string, targetCallback: IBannerEventListener): IBannerEventListener {
    return function(resp: IBannerResponse) {
        if (resp && resp.place === targetPlace) {
            targetCallback.call(null, resp);
        }
    };
}

/**
 * Сигнализирует слушателей о наступлении конкретного события
 */
function publishEvent(eventName: string, eventData: IBannerResponse) {
    logEvent(eventName, eventData);
    if (!LISTENERS_BY_EVENTS.hasOwnProperty(eventName)) {
        return;
    }
    const listeners = LISTENERS_BY_EVENTS[eventName];
    for (let i = 0, len = listeners.length; i < len; i++) {
        const listener = listeners[i];
        if (typeof listener === 'function') {
            listener(eventData);
        }
    }
}

function logEvent(eventName: string, eventData: IBannerResponse) {
    consoleLog(eventName, eventData);
    const { result } = eventData;
    let bannerId = eventData.place + '.' + eventData.slot;
    if (result && (result as BannerDataText).rbJson && (result as BannerDataText).rbJson![0].source) {
        bannerId += '.' + (result as BannerDataText).rbJson![0].source;
    }
    const eventId = eventData.transport + '.' + eventName;
    getBannerStatLogger().success('banner.event', bannerId, eventId);
}

function consoleLog(eventName: string, eventData: IBannerResponse) {
    if (isDebugMode()) {
        // eslint-disable-next-line no-console
        console.log('[BannerPlaceListeners] calling event \'' + eventName
            + '\', transport: \'' + eventData.transport
            + '\', place: \'' + eventData.place
            + '\', slot: ' + eventData.slot
            + '\', result: ' + eventData.result
        );
    }
}
